import { useState } from "react";
import {
  RiUserSettingsLine,
  RiNotificationLine,
  RiLockLine,
  RiPaletteLine,
  RiGlobalLine,
  RiShieldLine,
} from "react-icons/ri";

const Profile = () => {
  const [activeTab, setActiveTab] = useState("general");

  const tabs = [
    { id: "general", label: "General", icon: RiUserSettingsLine },
    { id: "notifications", label: "Notifications", icon: RiNotificationLine },
    { id: "security", label: "Security", icon: RiLockLine },
    { id: "appearance", label: "Appearance", icon: RiPaletteLine },
    { id: "language", label: "Language", icon: RiGlobalLine },
    { id: "privacy", label: "Privacy", icon: RiShieldLine },
  ];

  const userProfile = {
    name: "John Doe",
    email: "john@example.com",
    role: "Administrator",
    avatar: "JD",
    joinDate: "January 2023",
    phone: "+1 (555) 123-4567",
    company: "Tech Corp",
    location: "New York, USA",
    timezone: "Eastern Time (ET)",
    bio: "Senior Marketing Manager with 5+ years of experience in digital marketing and campaign management.",
    socialLinks: {
      linkedin: "linkedin.com/in/johndoe",
      twitter: "twitter.com/johndoe",
    },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-secondary-900">
          Profile Settings
        </h1>
        <p className="mt-2 text-secondary-600">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          {/* Profile Card */}
          <div className="card mb-6">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center text-white text-2xl font-bold">
                {userProfile.avatar}
              </div>
              <h2 className="mt-4 text-xl font-semibold text-secondary-900">
                {userProfile.name}
              </h2>
              <p className="text-secondary-600">{userProfile.role}</p>
              <p className="text-sm text-secondary-500 mt-1">
                Member since {userProfile.joinDate}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-4 py-2 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? "bg-primary-50 text-primary-600"
                    : "text-secondary-600 hover:bg-secondary-50"
                }`}
              >
                <tab.icon className="w-5 h-5 mr-3" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {activeTab === "general" && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-6">
                General Information
              </h3>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">
                      Full Name
                    </label>
                    <input
                      type="text"
                      className="mt-1 input-field"
                      defaultValue={userProfile.name}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">
                      Email
                    </label>
                    <input
                      type="email"
                      className="mt-1 input-field"
                      defaultValue={userProfile.email}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      className="mt-1 input-field"
                      defaultValue={userProfile.phone}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">
                      Company
                    </label>
                    <input
                      type="text"
                      className="mt-1 input-field"
                      defaultValue={userProfile.company}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">
                      Location
                    </label>
                    <input
                      type="text"
                      className="mt-1 input-field"
                      defaultValue={userProfile.location}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">
                      Timezone
                    </label>
                    <select className="mt-1 input-field">
                      <option value="ET">{userProfile.timezone}</option>
                      {/* More timezone options */}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">
                    Bio
                  </label>
                  <textarea
                    className="mt-1 input-field h-24"
                    defaultValue={userProfile.bio}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Social Links
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-secondary-500">LinkedIn:</span>
                      <input
                        type="text"
                        className="input-field flex-1"
                        defaultValue={userProfile.socialLinks.linkedin}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-secondary-500">Twitter:</span>
                      <input
                        type="text"
                        className="input-field flex-1"
                        defaultValue={userProfile.socialLinks.twitter}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">
                    Role
                  </label>
                  <input
                    type="text"
                    className="mt-1 input-field"
                    defaultValue={userProfile.role}
                    disabled
                  />
                </div>
                <div className="pt-4 flex justify-end space-x-3">
                  <button className="btn btn-secondary">Cancel</button>
                  <button className="btn btn-primary">Save Changes</button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "notifications" && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-6">
                Notification Preferences
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-secondary-900">
                      Email Notifications
                    </h4>
                    <p className="text-sm text-secondary-600">
                      Receive email updates about your activity
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      defaultChecked
                    />
                    <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
                {/* Notification settings */}
              </div>
            </div>
          )}

          {activeTab === "security" && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-6">
                Security Settings
              </h3>
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium text-secondary-900 mb-4">
                    Change Password
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">
                        Current Password
                      </label>
                      <input type="password" className="mt-1 input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">
                        New Password
                      </label>
                      <input type="password" className="mt-1 input-field" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700">
                        Confirm New Password
                      </label>
                      <input type="password" className="mt-1 input-field" />
                    </div>
                    <div>
                      <button className="btn btn-primary">
                        Update Password
                      </button>
                    </div>
                  </div>
                </div>
                <div className="border-t border-secondary-200 pt-6">
                  <h4 className="font-medium text-secondary-900 mb-4">
                    Two-Factor Authentication
                  </h4>
                  <p className="text-secondary-600 mb-4">
                    Add an extra layer of security to your account
                  </p>
                  <button className="btn btn-secondary">Enable 2FA</button>
                </div>
              </div>
            </div>
          )}

          {activeTab === "appearance" && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-6">
                Appearance Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Theme
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {["Light", "Dark", "System"].map((theme) => (
                      <button
                        key={theme}
                        className="p-4 border border-secondary-200 rounded-lg text-center hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {theme}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "language" && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-6">
                Language Settings
              </h3>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Select Language
                </label>
                <select className="input-field">
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === "privacy" && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-6">
                Privacy Settings
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-secondary-900">
                      Profile Visibility
                    </h4>
                    <p className="text-sm text-secondary-600">
                      Control who can see your profile
                    </p>
                  </div>
                  <select className="input-field w-40">
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                    <option value="team">Team Only</option>
                  </select>
                </div>
                {/* Privacy settings */}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
